import { Entity, LiveData, ObjectPool } from '@toeverything/infra';
import { catchError, from, map, of, startWith, switchMap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { PDFMeta } from '../renderer';
import { PDFRenderer } from '../renderer';
import { PDFPage } from './pdf-page';

export enum PDFStatus {
  IDLE = 0,
  Opening,
  Opened,
  Error,
}

export type PDFRendererState =
  | {
      status: PDFStatus.IDLE | PDFStatus.Opening;
    }
  | {
      status: PDFStatus.Opened;
      meta: PDFMeta;
    }
  | {
      status: PDFStatus.Error;
      error: Error;
    };

// Document meta (page count/sizes) per blob, surviving entity disposal. A
// remounted viewer can lay out and show cached page bitmaps immediately while
// the worker re-opens the document in the background. Blob ids are stable per
// uploaded file, so an entry only ever gets overwritten with the same values.
const pdfMetaCache = new Map<string, PDFMeta>();

export class PDF extends Entity<{ blobId: string }> {
  public readonly id: string = this.props.blobId;
  readonly renderer = new PDFRenderer();
  readonly pages = new ObjectPool<string, PDFPage>({
    onDelete: page => page.dispose(),
  });

  readonly state$ = LiveData.from<PDFRendererState>(
    from(
      this.workspaceService.workspace.docCollection.blobSync
        .get(this.id)
        .then(blob => blob?.arrayBuffer() ?? null)
    ).pipe(
      switchMap(data => {
        if (data) {
          // PDFium opens from a complete Uint8Array. The blob cache streams
          // remote bytes into OPFS first, but renderer handoff remains full
          // buffer by PDFium contract.
          return this.renderer.ob$('open', { data });
        }

        throw new Error('PDF not found');
      }),
      map((meta): PDFRendererState => {
        pdfMetaCache.set(this.id, meta);
        return {
          status: PDFStatus.Opened,
          meta,
        };
      }),
      startWith(
        ((): PDFRendererState => {
          // Known document → report Opened with the cached meta right away so
          // viewers lay out instantly; the fresh open replaces it (and render
          // calls simply wait on the worker pipeline until the doc is ready).
          const cachedMeta = pdfMetaCache.get(this.props.blobId);
          return cachedMeta
            ? { status: PDFStatus.Opened, meta: cachedMeta }
            : { status: PDFStatus.Opening };
        })()
      ),
      catchError((error: Error) =>
        of({ status: PDFStatus.Error, error } as PDFRendererState)
      )
    ),
    { status: PDFStatus.IDLE }
  );

  constructor(private readonly workspaceService: WorkspaceService) {
    super();
    this.disposables.push(() => this.pages.clear());
  }

  page(pageNum: number, size: string) {
    const key = `${pageNum}:${size}`;
    let rc = this.pages.get(key);

    if (!rc) {
      rc = this.pages.put(
        key,
        this.framework.createEntity(PDFPage, { pdf: this, pageNum })
      );
    }

    return { page: rc.obj, release: rc.release };
  }

  override dispose() {
    this.renderer.destroy();
    super.dispose();
  }
}
